import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEmployee } from 'context/EmployeeContext';
import {
    getTimeOffBalances,
    getMyTimeOffPolicies,
    getMyTimeOffRequests,
    submitTimeOffRequest,
    cancelTimeOffRequest,
} from 'services/employeePortal';
import { createNotification } from 'services/timeOff';
import { getActiveCompanyId } from 'utils/companyContext';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_STYLES = {
    pending:  { bg: '#fff3cd', color: '#856404' },
    approved: { bg: '#d1e7dd', color: '#0a3622' },
    denied:   { bg: '#f8d7da', color: '#842029' },
    paid:     { bg: '#e9ecef', color: '#333' },
};

const POLICY_COLORS = ['#1B75F0', '#1565C0', '#2e7d32', '#880e4f', '#6f42c1', '#0d6efd'];

export default function EmployeeTimeOff() {
    const { employeeId } = useEmployee();
    const queryClient = useQueryClient();

    const [showForm, setShowForm]   = useState(false);
    const [form, setForm]           = useState({ policyId: '', startDate: '', endDate: '', totalHours: '', reason: '' });
    const [formError, setFormError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const { data: balances = [], isLoading: loadingBal } = useQuery({
        queryKey: ['ep-to-balances', employeeId],
        queryFn: () => getTimeOffBalances(employeeId),
        enabled: !!employeeId,
        staleTime: 60 * 1000,
    });

    const { data: policies = [] } = useQuery({
        queryKey: ['ep-to-policies'],
        queryFn: getMyTimeOffPolicies,
        staleTime: 5 * 60 * 1000,
    });

    const { data: requests = [], isLoading: loadingReq } = useQuery({
        queryKey: ['ep-to-requests', employeeId],
        queryFn: () => getMyTimeOffRequests(employeeId),
        enabled: !!employeeId,
        staleTime: 30 * 1000,
    });

    const policyMap = useMemo(() => {
        const m = {};
        policies.forEach(p => { m[p.id] = p; });
        return m;
    }, [policies]);

    const balanceMap = useMemo(() => {
        const m = {};
        balances.forEach(b => { m[b.policyId] = b; });
        return m;
    }, [balances]);

    const activePolicies = policies.filter(p => p.isActive !== false);

    const { mutate: doSubmit, isPending: submitting } = useMutation({
        mutationFn: () => submitTimeOffRequest({
            employeeId,
            policyId:   form.policyId,
            startDate:  form.startDate,
            endDate:    form.endDate || form.startDate,
            totalHours: parseFloat(form.totalHours) || 0,
            reason:     form.reason,
            status:     'pending',
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ep-to-requests', employeeId] });
            queryClient.invalidateQueries({ queryKey: ['ep-to-balances', employeeId] });
            // Notify admin
            const policy = activePolicies.find(p => p.id === form.policyId);
            createNotification({
                recipientType: 'company',
                recipientId: getActiveCompanyId(),
                type: 'time_off_submitted',
                title: 'New Time Off Request',
                message: `An employee submitted a ${policy?.timeOffType ?? 'time off'} request for ${form.totalHours} hrs (${form.startDate}${form.endDate && form.endDate !== form.startDate ? ' – ' + form.endDate : ''}).`,
                link: '/time-off',
            }).catch(() => {});
            setShowForm(false);
            setForm({ policyId: '', startDate: '', endDate: '', totalHours: '', reason: '' });
            setFormError('');
            setSuccessMsg('Time off request submitted successfully!');
            setTimeout(() => setSuccessMsg(''), 4000);
        },
        onError: (err) => setFormError(err?.response?.data?.message || err?.message || 'Failed to submit request.'),
    });

    const { mutate: doCancel } = useMutation({
        mutationFn: (id) => cancelTimeOffRequest(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ep-to-requests', employeeId] });
            queryClient.invalidateQueries({ queryKey: ['ep-to-balances', employeeId] });
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.policyId)   return setFormError('Please select a time off policy.');
        if (!form.startDate)  return setFormError('Please select a start date.');
        if (!form.totalHours) return setFormError('Please enter the number of hours.');
        // Check if policy disallows negative balance and employee has no balance
        const selectedPolicy = policies.find(p => p.id === form.policyId);
        const availableBalance = balanceMap[form.policyId]?.balanceHours ?? 0;
        if (selectedPolicy?.allowNegativeBalance === false && availableBalance <= 0) {
            return setFormError(`You have no remaining balance for "${selectedPolicy.name}". This policy does not allow requests beyond your available balance.`);
        }
        if (selectedPolicy?.allowNegativeBalance === false && Number(form.totalHours) > availableBalance) {
            return setFormError(`You only have ${availableBalance} hrs available for "${selectedPolicy.name}". This policy does not allow requests beyond your balance.`);
        }
        setFormError('');
        doSubmit();
    };

    const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const sortedRequests = [...requests].sort((a, b) => new Date(b.createdAt || b.startDate) - new Date(a.createdAt || a.startDate));

    return (
        <div className="content" style={{ padding: '24px' }}>
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1">Time Off</h4>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>View your balances and request time off</p>
                </div>
                <button
                    className="btn btn-sm"
                    style={{ backgroundColor: '#1B75F0', color: '#fff', fontWeight: 600, padding: '8px 20px' }}
                    onClick={() => { setShowForm(true); setFormError(''); }}
                >
                    + Request Time Off
                </button>
            </div>

            {successMsg && (
                <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>{successMsg}</div>
            )}

            {/* ── Balances ── */}
            <h6 className="fw-semibold mb-3" style={{ fontSize: 14, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>Available Balances</h6>

            {loadingBal ? (
                <p className="text-muted" style={{ fontSize: 13 }}>Loading balances...</p>
            ) : activePolicies.length === 0 ? (
                <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: 8, color: '#888', fontSize: 13, marginBottom: 24 }}>
                    No time off policies have been set up yet.
                </div>
            ) : (
                <div className="row g-3 mb-4">
                    {activePolicies.map((policy, i) => {
                        const bal     = balanceMap[policy.id];
                        const avail   = bal?.balanceHours ?? 0;
                        const pending = bal?.pendingHours ?? 0;
                        const color   = POLICY_COLORS[i % POLICY_COLORS.length];
                        return (
                            <div key={policy.id} className="col-6 col-md-4 col-lg-3">
                                <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '16px 18px', borderTop: `4px solid ${color}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                    <div style={{ fontSize: 12, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
                                        {policy.name}
                                    </div>
                                    <div style={{ fontSize: 28, fontWeight: 700, color }}>
                                        {Number(avail).toFixed(1)}
                                        <span style={{ fontSize: 14, fontWeight: 400, color: '#888', marginLeft: 4 }}>hrs</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, flexWrap: 'wrap', gap: 4 }}>
                                        <span style={{ fontSize: 11, color: '#aaa' }}>{policy.timeOffType || 'Time Off'}</span>
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {pending > 0 && (
                                                <span style={{ fontSize: 11, fontWeight: 600, color: '#856404', background: '#fff3cd', borderRadius: 6, padding: '1px 7px' }}>
                                                    {Number(pending).toFixed(1)} pending
                                                </span>
                                            )}
                                            {policy.allowNegativeBalance === false && bal !== undefined && avail <= 0 && (
                                                <span style={{ fontSize: 11, fontWeight: 600, color: '#842029', background: '#f8d7da', borderRadius: 6, padding: '1px 7px' }}>
                                                    No requests allowed
                                                </span>
                                            )}
                                            {policy.allowNegativeBalance === false && bal !== undefined && avail > 0 && (
                                                <span style={{ fontSize: 11, fontWeight: 600, color: '#664d03', background: '#fff3cd', borderRadius: 6, padding: '1px 7px' }} title="Cannot request more than available balance">
                                                    Balance limited
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Request Form Modal ── */}
            {showForm && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
                >
                    <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h5 className="mb-0 fw-semibold" style={{ fontSize: 16 }}>Request Time Off</h5>
                            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div style={{ padding: '20px 24px' }}>
                                {formError && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{formError}</div>}

                                <div className="mb-3">
                                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Time Off Policy *</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={form.policyId}
                                        onChange={e => setField('policyId', e.target.value)}
                                        required
                                    >
                                        <option value="">— Select policy —</option>
                                        {activePolicies.map(p => {
                                            const avail = balanceMap[p.id]?.balanceHours ?? 0;
                                            const blocked = p.allowNegativeBalance === false && avail <= 0;
                                            return (
                                                <option key={p.id} value={p.id} disabled={blocked}>
                                                    {p.name} ({Number(avail).toFixed(1)} hrs available){blocked ? ' — No balance remaining' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div className="row g-2 mb-3">
                                    <div className="col">
                                        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Start Date *</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={form.startDate}
                                            onChange={e => setField('startDate', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col">
                                        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>End Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={form.endDate}
                                            min={form.startDate}
                                            onChange={e => setField('endDate', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Hours Requested *</label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. 8"
                                        min="0.5"
                                        step="0.5"
                                        value={form.totalHours}
                                        onChange={e => setField('totalHours', e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="mb-2">
                                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Reason <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
                                    <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        placeholder="Brief description..."
                                        value={form.reason}
                                        onChange={e => setField('reason', e.target.value)}
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>
                            </div>
                            <div style={{ padding: '14px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                                <button
                                    type="submit"
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#1B75F0', color: '#fff', fontWeight: 600 }}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Request History ── */}
            <h6 className="fw-semibold mb-3" style={{ fontSize: 14, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>My Requests</h6>

            {loadingReq ? (
                <p className="text-muted" style={{ fontSize: 13 }}>Loading requests...</p>
            ) : sortedRequests.length === 0 ? (
                <div style={{ padding: '32px', background: '#f8f9fa', borderRadius: 10, textAlign: 'center', color: '#888', fontSize: 13 }}>
                    No time off requests yet. Click <strong>+ Request Time Off</strong> to get started.
                </div>
            ) : (
                <div style={{ border: '1px solid #dee2e6', borderRadius: 10, overflow: 'hidden' }}>
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                        <thead style={{ background: '#f8f9fa' }}>
                            <tr>
                                <th className="ps-3">Policy</th>
                                <th>Dates</th>
                                <th className="text-end">Hours</th>
                                <th>Reason</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRequests.map(req => {
                                const policy = policyMap[req.policyId];
                                const statusStyle = STATUS_STYLES[req.status?.toLowerCase()] || STATUS_STYLES.pending;
                                const canCancel = req.status?.toLowerCase() === 'pending';
                                const dateRange = req.endDate && req.endDate !== req.startDate
                                    ? `${fmtDate(req.startDate)} – ${fmtDate(req.endDate)}`
                                    : fmtDate(req.startDate);
                                return (
                                    <tr key={req.id}>
                                        <td className="ps-3 fw-medium">{policy?.name || '—'}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{dateRange}</td>
                                        <td className="text-end">{Number(req.totalHours ?? 0).toFixed(1)} hrs</td>
                                        <td style={{ color: '#666', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {req.reason || '—'}
                                        </td>
                                        <td>
                                            <span style={{ ...statusStyle, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                                {req.status ? req.status.charAt(0).toUpperCase() + req.status.slice(1).toLowerCase() : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            {canCancel && (
                                                <button
                                                    onClick={() => doCancel(req.id)}
                                                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 12, padding: '0 6px' }}
                                                    title="Cancel request"
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
